/**
 * SkyViewLut — Hillaire 2020 sky-view LUT.
 *
 * The sky-view LUT is the per-frame product the background node samples
 * directly. It stores, for every view direction (encoded as
 * azimuth-to-sun × non-linear zenith via `skyViewUV`), the full
 * single-scattering + multiple-scattering radiance of the atmosphere as seen
 * from the camera's current altitude with the sun at its current position.
 *
 * Size: 192×108 (Hillaire's canonical lat-long-ish layout). The zenith axis
 * is warped toward the horizon (sqrt remap, two halves) so the steep colour
 * gradient at the horizon is well-resolved with few texels — see
 * `skyViewUV` in `../lutParameterization.ts`.
 *
 * Each texel ray-marches `steps` segments from the camera through the
 * atmosphere (Hillaire §6). At each sample it adds:
 *   - single scattering:   σ_s · phase(θ_VL) · T_sun  · throughput
 *   - multiple scattering:  σ_s · Ψ_ms(r, µ_s)         · throughput
 * where T_sun is read from the transmittance LUT and Ψ_ms from the
 * multi-scatter LUT. The view throughput accumulates the segment
 * transmittance. The sun disc is NOT baked here — it is added analytically
 * in the background node so it stays crisp regardless of LUT resolution.
 *
 * Unlike the transmittance/multi-scatter LUTs (which depend only on the
 * atmosphere config), the sky-view LUT depends on sun direction AND camera
 * altitude, so it is re-baked when EITHER changes beyond a threshold (see
 * SkyLutSet dirty-flagging) — not every frame.
 *
 * Reference: Hillaire 2020, §6 (Sky-View LUT) + eqs. (3),(4).
 */

import type * as THREE from 'three/webgpu';
import type { AtmosphereConfig, Vec3 } from '../AtmosphereConfig.js';
import {
  computeDensities, rayleighPhase, cornetteShanksPhase,
} from '../AtmosphereConfig.js';
import {
  skyViewUVInverse, rayAtmosphereIntersect,
} from '../lutParameterization.js';
import { transmittanceUV } from '../lutParameterization.js';
import { multiScatterUV } from './MultiScatterLut.js';
import {
  makeFloatTexture, sampleTransmittanceLut, clamp01, rayNearIntersect,
} from './TransmittanceLut.js';
import { sampleMultiScatterLut } from './MultiScatterLut.js';

/** Canonical Hillaire sky-view LUT size. */
export const SKYVIEW_LUT_WIDTH = 192;
export const SKYVIEW_LUT_HEIGHT = 108;

/** Ray-march steps per sky-view texel. */
export const SKYVIEW_MARCH_STEPS = 32;

export interface SkyViewContext {
  cfg: AtmosphereConfig;
  /** Camera radius from planet centre (km). */
  viewHeight: number;
  /** Sun zenith cosine in the camera's local up-frame. */
  cosSunZenith: number;
  /** Baked transmittance LUT. */
  transmittanceData: Float32Array;
  transmittanceWidth: number;
  transmittanceHeight: number;
  /** Baked multi-scatter LUT (square, side = size). */
  multiScatterData: Float32Array;
  multiScatterSize: number;
  /** Sun illuminance multiplier. */
  sunIlluminance: number;
}

/**
 * CPU reference: single + multiple scattering radiance for one view
 * direction, given its zenith cosine (µ = dot(V, up)) and the cosine of the
 * angle between the view ray and the sun (cosVL = dot(V, L)).
 *
 * The sun's local-frame direction is L = (sqrt(1−c²), c, 0) with
 * c = cosSunZenith; we derive the per-sample sun-zenith cosine for the LUT
 * lookups from the geometry of V and L (flat-frame approximation: the local
 * "up" stays constant along the short atmospheric path, as in Hillaire's
 * sky-view LUT).
 */
export function evaluateSkyViewRadiance(
  ctx: SkyViewContext,
  cosViewZenith: number,
  cosLightView: number,
): Vec3 {
  const { cfg } = ctx;
  const r0 = ctx.viewHeight;
  const mu = cosViewZenith;

  // Ray length: to top of atmosphere, or to the ground if it intersects.
  // The ground test uses the NEAREST surface root (rayNearIntersect) — the
  // far root would march the ray through the planet and sample sub-surface
  // densities that overflow the exponential extinction.
  const tTop = rayAtmosphereIntersect(r0, mu, cfg.topRadius);
  const tGround = rayNearIntersect(r0, mu, cfg.bottomRadius);
  let tMax = tTop;
  let hitGround = false;
  if (tGround > 0 && tGround < tTop) {
    tMax = tGround;
    hitGround = true;
  }
  if (tMax <= 0) return { x: 0, y: 0, z: 0 };

  // Phase functions depend on the scatter angle θ_VL.
  const phaseR = rayleighPhase(cosLightView);
  const phaseM = cornetteShanksPhase(cosLightView, cfg.miePhaseG);

  const steps = SKYVIEW_MARCH_STEPS;
  const dt = tMax / steps;

  let lx = 0, ly = 0, lz = 0;     // accumulated radiance
  let trX = 1, trY = 1, trZ = 1;  // view throughput

  for (let s = 0; s < steps; s++) {
    const t = (s + 0.5) * dt;
    const rAtSample = Math.sqrt(r0 * r0 + 2.0 * r0 * mu * t + t * t);
    const altitudeKm = Math.max(0, rAtSample - cfg.bottomRadius);
    const d = computeDensities(cfg, altitudeKm);

    // Scattering coefficients at the sample.
    const sigSrX = cfg.rayleighScattering.x * d.rayleigh;
    const sigSrY = cfg.rayleighScattering.y * d.rayleigh;
    const sigSrZ = cfg.rayleighScattering.z * d.rayleigh;
    const sigSmX = cfg.mieScattering.x * d.mie;
    const sigSmY = cfg.mieScattering.y * d.mie;
    const sigSmZ = cfg.mieScattering.z * d.mie;

    // Total extinction for the view throughput.
    const extX = cfg.rayleighScattering.x * d.rayleigh
      + cfg.mieExtinction.x * d.mie + cfg.ozoneExtinction.x * d.ozone;
    const extY = cfg.rayleighScattering.y * d.rayleigh
      + cfg.mieExtinction.y * d.mie + cfg.ozoneExtinction.y * d.ozone;
    const extZ = cfg.rayleighScattering.z * d.rayleigh
      + cfg.mieExtinction.z * d.mie + cfg.ozoneExtinction.z * d.ozone;

    // Sun transmittance from this sample (flat-frame: sun-zenith cosine
    // approximately constant; use the camera-frame cosSunZenith).
    const tUV = transmittanceUV(cfg, rAtSample, ctx.cosSunZenith);
    const sunT = sampleTransmittanceLut(
      ctx.transmittanceData, ctx.transmittanceWidth, ctx.transmittanceHeight,
      tUV.u, tUV.v,
    );

    // Multiple-scattering ambient term Ψ_ms.
    const msUV = multiScatterUV(cfg, rAtSample, ctx.cosSunZenith);
    const psi = sampleMultiScatterLut(
      ctx.multiScatterData, ctx.multiScatterSize, msUV.u, msUV.v,
    );

    // In-scatter: single (phase·T_sun) + multi (isotropic Ψ_ms).
    const inX = (sigSrX * phaseR + sigSmX * phaseM) * sunT.x + (sigSrX + sigSmX) * psi.x;
    const inY = (sigSrY * phaseR + sigSmY * phaseM) * sunT.y + (sigSrY + sigSmY) * psi.y;
    const inZ = (sigSrZ * phaseR + sigSmZ * phaseM) * sunT.z + (sigSrZ + sigSmZ) * psi.z;

    // Energy-conserving integration of in-scatter over the segment
    // (Hillaire §6: analytic per-segment integral of S·T over dt). We use
    // the simpler midpoint accumulation S·throughput·dt which matches the
    // v1 inline march for cross-validation.
    lx += trX * inX * dt;
    ly += trY * inY * dt;
    lz += trZ * inZ * dt;

    trX *= Math.exp(-extX * dt);
    trY *= Math.exp(-extY * dt);
    trZ *= Math.exp(-extZ * dt);
  }

  // Optional ground contribution (bounced sunlight) when the ray hits the
  // surface — Lambertian albedo × sun transmittance × N·L. Kept simple.
  if (hitGround) {
    const groundR = cfg.bottomRadius;
    const tUV = transmittanceUV(cfg, groundR, ctx.cosSunZenith);
    const sunT = sampleTransmittanceLut(
      ctx.transmittanceData, ctx.transmittanceWidth, ctx.transmittanceHeight,
      tUV.u, tUV.v,
    );
    const nDotL = Math.max(0, ctx.cosSunZenith);
    const a = cfg.groundAlbedo;
    lx += trX * sunT.x * a.x * nDotL / Math.PI;
    ly += trY * sunT.y * a.y * nDotL / Math.PI;
    lz += trZ * sunT.z * a.z * nDotL / Math.PI;
  }

  return { x: lx * ctx.sunIlluminance, y: ly * ctx.sunIlluminance, z: lz * ctx.sunIlluminance };
}

export interface SkyViewLutResult {
  data: Float32Array;
  width: number;
  height: number;
  texture: THREE.DataTexture;
}

/** Bake the sky-view LUT (192×108 Float RGBA). */
export function bakeSkyViewLut(
  ctx: SkyViewContext,
  width = SKYVIEW_LUT_WIDTH,
  height = SKYVIEW_LUT_HEIGHT,
): SkyViewLutResult {
  const data = new Float32Array(width * height * 4);
  for (let y = 0; y < height; y++) {
    const v = (y + 0.5) / height;
    for (let x = 0; x < width; x++) {
      const u = (x + 0.5) / width;
      const { cosViewZenith, cosLightView } = skyViewUVInverse(ctx.cfg, ctx.viewHeight, u, v);
      const rad = evaluateSkyViewRadiance(ctx, cosViewZenith, cosLightView);
      const i = (y * width + x) * 4;
      data[i + 0] = rad.x;
      data[i + 1] = rad.y;
      data[i + 2] = rad.z;
      data[i + 3] = 1.0;
    }
  }
  const texture = makeFloatTexture(data, width, height, 'SkySkyViewLUT');
  return { data, width, height, texture };
}

/** Bilinear sample of a baked sky-view LUT (mirrors GPU LinearFilter). */
export function sampleSkyViewLut(
  data: Float32Array, width: number, height: number, u: number, v: number,
): Vec3 {
  return sampleTransmittanceLut(data, width, height, clamp01(u), clamp01(v));
}
